import { openModal } from "action/modal/openModal";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import styles from "component/category/product-cards/productCards.module.scss";
import PAText from "component/design-system/typography/PAText/PAText";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import { VEHICLE_FITMENT } from "constant/shared-constants";
import { RootState } from "interfaces/generics.type";
import { IVehcileDefaultValues } from "interfaces/session/IVehicle";
import NoSsr from "modules/NoSsr";
import { CUSTOM_SIZES_WARNINGS } from "modules/category/constants/customSizesConstants";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { isOEHP } from "util/helper/helper";
import { LINE_PAGES } from "util/product/product-constants";
import { getVehicleSelectionOrder } from "util/vehicle/get-vehicle-selection-order";
import getFocusEl from "util/vehicle/getFocusEl";

const ProductFitmentUniversal = dynamic(() => import(/* webpackChunkName: "pf-universal-desktop" */ "modules/fitment/ProductFitmentUniversal"));
const ProductFitment = dynamic(() => import(/* webpackChunkName: "pf-product-fitment" */ "modules/fitment/ProductFitment"));

interface IProps {
  product: ICompactProduct;
  defaultVehicle: IVehcileDefaultValues;
}
// used on prtoduct page
function CategoryFitmentContainer(props: IProps) {
  const {
    product, defaultVehicle = {}, fitmentOptionsDetails = {}, validateFitment, hideFitment,
  } = props;
  const { fitment, remainingFitmentOptions, fitmentStatus } = product;
  const isCustomSizeWarning = CUSTOM_SIZES_WARNINGS.includes(fitmentStatus);
  const [nextAttribute] = remainingFitmentOptions;

  const dispatch = useDispatch();
  const { t } = useTranslation("category");
  const { selectedVehicle, defaultValues } = useSelector((state: RootState) => state.garage);
  const { currentPageType } = useSelector((store: RootState) => store.utils);
  const { asPath } = useRouter();
  const isOEPage = isOEHP(asPath);

  const updatedDefaultVehicle = {
    defaultMake: defaultVehicle.defaultMake || defaultValues.defaultMake,
    defaultModel: defaultVehicle.defaultModel || defaultValues.defaultModel,
    defaultYear: defaultVehicle.defaultYear || defaultValues.defaultYear,
  };

  const selectionOrder = getVehicleSelectionOrder(updatedDefaultVehicle.defaultMake,
    updatedDefaultVehicle.defaultModel, updatedDefaultVehicle.defaultYear);
  const focusElement = getFocusEl(selectionOrder, { ...updatedDefaultVehicle });

  // Issue: infinite loader on product card on category page
  // Example: https://partsavatar.ca/dodge-dakota-doors?year=2009
  // get-page-details API returns requiredFitmentOption, while vehicle-details does not bcz category is being passed in it
  // Solution: Add default title if requiredFitmentOption is ep.Engine

  if (isOEPage) {
    return null;
  }

  if (fitment === VEHICLE_FITMENT.UNIVERSAL) {
    return (
      <ProductFitmentUniversal iconSize="small" className={styles.universal_product} />
    );
  }

  if (!isCustomSizeWarning && !selectedVehicle && fitment !== VEHICLE_FITMENT.UNIVERSAL) {
    return (
      <div
        className={`${styles.warning_icon} d-flex`}
        onClick={() => {
          dispatch(openModal("vehicleSelector", {
            ...updatedDefaultVehicle, focusElement, selectionOrder, forceTrigger: true,
          }));
        }}
      >
        <i className={`fa fa-exclamation-triangle ${styles.isWarning} `} />
        &nbsp; &nbsp;
        <PAText component="span" color="success-secondary">
          {t("guarantee_fit")}
        </PAText>
      </div>
    );
  }

  // Fitment option needs to be asked but fitment title is not set yet.
  // Fitment title is set by vehicle-details API from vehicle selector modal
  // We don't have fitment info on line page

  const attributeTitle = fitmentOptionsDetails[remainingFitmentOptions[0]]?.title;

  if (nextAttribute && !attributeTitle && !LINE_PAGES.includes(currentPageType)) {
    return <LoaderSkeleton height={40} />;
  }

  return (
    <NoSsr>
      <ProductFitment
        product={product}
        showHeading={false}
        className={styles.fitment_container}
        iconSize="small"
        fitmentOptionsDetails={fitmentOptionsDetails}
        validateFitment={validateFitment}
        hideFitment={hideFitment}
      />
    </NoSsr>
  );
}
export default CategoryFitmentContainer;
